import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Factory, 
  Box, 
  Timer, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Droplets,
  RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  running: { label: "Em Execução", color: "status-running", textColor: "text-emerald-500" },
  idle: { label: "Parada", color: "status-idle", textColor: "text-amber-500" },
  stopped: { label: "Desligada", color: "status-stopped", textColor: "text-red-500" },
  maintenance: { label: "Manutenção", color: "status-maintenance", textColor: "text-indigo-500" },
};

function formatTime(seconds) {
  if (!seconds || seconds <= 0) return "00:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function MachineCard({ data }) {
  const { machine, active_order, part, mold, production } = data;
  const statusConfig = STATUS_CONFIG[machine.status] || STATUS_CONFIG.idle;
  
  const progress = active_order
    ? Math.min(100, (production.good_quantity / active_order.good_quantity_target) * 100)
    : 0;

  return (
    <Card className="border-border/50 card-hover transition-default" data-testid={`machine-card-${machine.code}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-3 w-3 rounded-full ${statusConfig.color} status-indicator`} />
            <CardTitle className="font-chivo text-lg">{machine.code}</CardTitle>
          </div>
          <Badge variant="outline" className={statusConfig.textColor}>
            {statusConfig.label}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{machine.description}</p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {active_order ? (
          <>
            {/* Order Info */}
            <div className="p-3 bg-secondary/50 rounded-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Ordem Ativa</span>
                <span className="font-mono text-sm font-bold">{active_order.code}</span>
              </div>
              {part && (
                <div className="flex items-center gap-2">
                  <Box className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{part.code} - {part.description}</span>
                </div>
              )}
              {mold && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>Molde: {mold.code}</span>
                  <span>•</span>
                  <span>{mold.cavities} cav.</span>
                  <span>•</span>
                  <span>{mold.cycle_time}s/ciclo</span>
                </div>
              )}
            </div>

            {/* Production Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-emerald-500/10 rounded-sm">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span className="text-xs text-muted-foreground">Produção Boa</span>
                </div>
                <p className="font-mono text-xl font-bold text-emerald-500">
                  {production.good_quantity.toLocaleString()}
                </p>
              </div>
              
              <div className="p-3 bg-red-500/10 rounded-sm">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span className="text-xs text-muted-foreground">Rejeitada</span>
                </div>
                <p className="font-mono text-xl font-bold text-red-500">
                  {production.rejected_quantity.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progresso</span>
                <span className="font-mono">
                  {production.good_quantity} / {active_order.good_quantity_target}
                </span>
              </div>
              <Progress value={progress} className="h-2" />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Em falta: {production.remaining.toLocaleString()}</span>
                <span>{progress.toFixed(1)}%</span>
              </div>
            </div>

            {/* Times */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Decorrido</p>
                  <p className="font-mono">{formatTime(production.elapsed_time)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Restante</p>
                  <p className="font-mono">{formatTime(production.remaining_time)}</p>
                </div>
              </div>
            </div>

            {/* Raw Material */}
            <div className="flex items-center gap-2 p-2 bg-secondary/30 rounded-sm">
              <Droplets className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">MP Consumida:</span>
              <span className="font-mono text-sm">{production.raw_material_consumed} kg</span>
            </div>
          </>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            <Factory className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Sem ordem ativa</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nextRefresh, setNextRefresh] = useState(30);

  const fetchDashboard = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setIsRefreshing(true);
      const response = await axios.get(`${API}/dashboard/factory`);
      setDashboardData(response.data);
      setLastUpdate(new Date());
      setNextRefresh(30);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    
    // Auto-refresh every 30 seconds to keep server awake
    const refreshInterval = setInterval(() => fetchDashboard(true), 30 * 1000);
    
    // Countdown timer
    const countdownInterval = setInterval(() => {
      setNextRefresh(prev => prev > 0 ? prev - 1 : 30);
    }, 1000);
    
    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [fetchDashboard]);

  const runningMachines = dashboardData?.machines?.filter(
    (m) => m.machine.status === "running"
  ).length || 0;
  
  const totalMachines = dashboardData?.machines?.length || 0;

  const totalGoodProduction = dashboardData?.machines?.reduce(
    (sum, m) => sum + (m.production?.good_quantity || 0),
    0
  ) || 0;

  const totalRejected = dashboardData?.machines?.reduce(
    (sum, m) => sum + (m.production?.rejected_quantity || 0),
    0
  ) || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight">
            Dashboard da Fábrica
          </h1>
          <p className="text-muted-foreground mt-1">
            Visão geral da produção em tempo real
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Auto-refresh indicator */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/50 px-3 py-1.5 rounded-full">
            {isRefreshing ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin text-primary" />
                <span>A atualizar...</span>
              </>
            ) : (
              <>
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                <span>Auto-refresh: {nextRefresh}s</span>
              </>
            )}
          </div>
          {lastUpdate && (
            <span className="text-sm text-muted-foreground hidden md:inline">
              {lastUpdate.toLocaleTimeString("pt-PT")}
            </span>
          )}
          <Button
            variant="outline"
            onClick={() => fetchDashboard(true)}
            disabled={isRefreshing}
            data-testid="refresh-dashboard"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Máquinas Ativas</p>
                <p className="font-chivo text-3xl font-bold">
                  {runningMachines}/{totalMachines}
                </p>
              </div>
              <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center">
                <Factory className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Produção Boa</p>
                <p className="font-chivo text-3xl font-bold text-emerald-500">
                  {totalGoodProduction.toLocaleString()}
                </p>
              </div>
              <div className="h-12 w-12 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rejeitada</p>
                <p className="font-chivo text-3xl font-bold text-red-500">
                  {totalRejected.toLocaleString()}
                </p>
              </div>
              <div className="h-12 w-12 rounded-sm bg-red-500/10 flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Taxa Qualidade</p>
                <p className="font-chivo text-3xl font-bold">
                  {totalGoodProduction + totalRejected > 0
                    ? ((totalGoodProduction / (totalGoodProduction + totalRejected)) * 100).toFixed(1)
                    : "100"}%
                </p>
              </div>
              <div className="h-12 w-12 rounded-sm bg-amber-500/10 flex items-center justify-center">
                <Box className="h-6 w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Machines Grid */}
      <div>
        <h2 className="font-chivo text-xl font-semibold mb-4">Estado das Máquinas</h2>
        {dashboardData?.machines?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {dashboardData.machines.map((machineData) => (
              <MachineCard key={machineData.machine.id} data={machineData} />
            ))}
          </div>
        ) : (
          <Card className="border-border/50">
            <CardContent className="py-12 text-center text-muted-foreground">
              <Factory className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Nenhuma máquina registada</p>
              <p className="text-sm">Configure as máquinas na secção de administração</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
